import { useCallback, useState } from 'react'
import Image from 'next/image'
import { useTranslation } from 'react-i18next'
import { useRouter } from 'next/router'

// Components
import { BlogCategoryChip } from '@/storyblokComponents/legacy/BlogCategoryChip'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'

// Helpers
import { PostListItem } from '@/domains/post'
import { dateToLocaleString } from '@/helpers/parseDate'
import { useTheme } from '@cellectenergy/cellect-react-components'
import { transformStoryblokImage } from '@/helpers/imageOptimization'

// Styles
import * as Styled from './PostCard.styles'

const PostCard = ({ post, className }: { post: PostListItem; className: string }) => {
  const { t } = useTranslation('global')
  const theme = useTheme()

  const router = useRouter()
  const [prefetched, setPrefetched] = useState(false)

  // TODO: optimize images for different screen sizes
  // const isMd = useMediaQuery(theme.breakpoints.down("md"));
  // const isLg = useMediaQuery(theme.breakpoints.down("lg"));
  // const isXl = useMediaQuery(theme.breakpoints.up("lg"));

  const getPreviewImageSrc = (post: any) => {
    // Helper function to get the best available format from an image object
    // Try preview_image first, then fallback to header_image, and finally default image
    return (
      post?.previewImage?.url || // Check preview_image formats
      post?.headerImage?.url || // Check header_image formats
      '/assets/blog/bgImage.webp' // Default image
    )
  }

  const getImageSrcOptimized = useCallback(
    (image: any) =>
      transformStoryblokImage(image, {
        width: 150,
        height: 150,
        format: 'webp',
      }),
    [post]
  )

  // Capture post.slug in lexical scope through closure
  const handleMouseEnter = () => {
    if (prefetched) return
    setPrefetched(true)

    // Slug is now accessed from the post prop in component scope
    if (post.slug) {
      router.prefetch(`/blog/${post.slug}`)
    } else {
      console.warn('Cannot prefetch - post slug is undefined')
    }
  }

  const handleCardClick = (slug: any) => {
    router.push(`/blog/${slug}`)
  }

  return (
    <Styled.BlogPost onMouseEnter={handleMouseEnter} onClick={() => handleCardClick(post.slug)}>
      <Styled.BlogPostBody>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            flex: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {post.author?.image?.filename && (
              <Styled.BlogPostAuthorImageContainer
                src={getImageSrcOptimized(post.author?.image?.filename)}
                alt="author"
                width={32}
                height={32}
              />
            )}
            <Typography variant="label-large" fontWeight={500} component={'div'}>
              By {post.author?.name ? post.author.name : 'Cellect'}
            </Typography>
          </Box>

          <Typography
            variant="title-medium"
            fontWeight={500}
            component={'div'}
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              lineHeight: 1.2, // Control line height for consistency
            }}
          >
            {post.title}
          </Typography>
        </Box>
        <Styled.BlogPostImageContainer className={className}>
          <Image
            src={getImageSrcOptimized(getPreviewImageSrc(post))}
            alt={post.title}
            fill
            priority={false} // Set to true if it's a critical image that should be preloaded
            loading="lazy" // Lazy load the image
            quality={100} // Optionally adjust image quality for performance
            style={{
              objectFit: 'cover',
              width: '100%', // Fluid width
              maxWidth: '100%', // Set max width
            }}
          />
        </Styled.BlogPostImageContainer>
      </Styled.BlogPostBody>

      <Styled.BlogPostFooter>
        {post?.tags && post.tags.map((tag: any) => <BlogCategoryChip selected key={tag} category={tag} />)}
        {post && (
          <Typography variant="label-large">{post.publishedAt && dateToLocaleString(post.publishedAt)}</Typography>
        )}

        <Typography color={theme.palette.common.corporateColors?.secondary.tiaraGray} variant="label-large">
          {t('Blog.minRead', { minutes: post.readTime })}
        </Typography>
      </Styled.BlogPostFooter>
    </Styled.BlogPost>
  )
}

export default PostCard
